/* eslint-disable new-cap */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import arrayMove from 'array-move';
import moment from 'moment';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import ButtonRadio, { ButtonItem } from '../../../../components/ButtonRadio';
import AngleDashboard from '../AngleDashboard';
import SpeedDashboard from '../SpeedDashboard';
import {
  ControlType,
  CommandType,
  ModuleName,
  ControlState,
  MaxSpeedMap,
  ModalInfoText,
  ControlSpeed,
  ControlDirection,
  controlRequestDuration,
  controlResponseDelay,
  maxVisibleDelayLimit,
} from '../../util';
import './index.scss';
import eventProvider from '../../../../event/eventProvider';
import AllActionType from '../../../../redux/actionType';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { reportSgmEvent, SgmEventType } from '@/utils/sgm';
interface Props {
  vehicleName: string;
  responseData: any;
  maxVideoDelay: number;
  localViewDelay: number;
  handleSendRemoteRequest: AnyFunc;
  visible: boolean;
}

const speedControlBtnList: string[] = [
  'go-ahead-icon',
  'stop-icon',
  'back-icon',
];

let timer: any = null;
let currentSliderIndex = 1;
const directArr = [1, 0, -1];
let requestId = 0;
const responseMap = new Map(); // 每一次响应数据的记录
const tipMessageMap = new Map([
  [CommandType.NORMAL, { iconCls: 'normal-icon', message: '安全模块监管中' }],
  [
    CommandType.SUPER,
    { iconCls: 'error-mode-icon', message: '脱离安全模块监管' },
  ],
]);

type ActionTrigger = 'click' | 'drag' | 'keyboard';

type OperationInfo = {
  action: string;
  trigger: ActionTrigger;
  key?: string;
};

const gearActionList = ['gear_forward', 'gear_neutral', 'gear_reverse'];
const speedActionMap: Record<ControlSpeed, string> = {
  [ControlSpeed.SLOW_DOWN]: 'speed_down',
  [ControlSpeed.SPEED_UP]: 'speed_up',
  [ControlSpeed.BRAKE]: 'brake',
};
const angleActionMap: Record<ControlDirection, string> = {
  [ControlDirection.TURN_LEFT]: 'turn_left',
  [ControlDirection.TURN_RIGHT]: 'turn_right',
  [ControlDirection.BACK_TO_NORMAL]: 'center_steering',
};

const SortableItem = SortableElement(({ value, sortIndex }: any) => {
  return (
    <div
      className={`slider-thumb ${
        currentSliderIndex == sortIndex ? 'slider-active' : ''
      } `}
    >
      {value}
    </div>
  );
});

const Sortable = SortableContainer(({ items }: any) => {
  return (
    <div className='slider-track'>
      {items.map((item: number, index: number) => (
        <SortableItem
          key={index}
          index={index}
          sortIndex={index}
          value={item}
        />
      ))}
    </div>
  );
});

const Control = (props: Props) => {
  const {
    vehicleName,
    maxVideoDelay,
    localViewDelay,
    responseData,
    handleSendRemoteRequest,
    visible,
  } = props;
  const [btnGroup, setBtnGroup] = useState([
    {
      label: '安全遥控',
      isActive: true,
      value: CommandType.NORMAL,
    },
    {
      label: '强制遥控',
      isActive: false,
      value: CommandType.SUPER,
    },
  ]);
  const [speedControl, setSpeedControl] = useState([...Array(3)]);
  const [speed, setSpeed] = useState(0);
  const [angle, setAngle] = useState(0);
  const [canShowConfirm, setCanShowConfirm] = useState(true);
  const [updateFlag, forceUpdate] = useState(false);
  const [commandType, setCommandType] = useState(CommandType.NORMAL);
  const speedDashboardRef = useRef<SpeedDashboard>(null);
  const angleDashboardRef = useRef<AngleDashboard>(null);
  const timeoutRequestIdRef = useRef<number | null>(null);

  const reportRemoteControlAction = (
    type: SgmEventType,
    action: string,
    trigger: ActionTrigger | 'system',
    result: string,
    options: {
      key?: string;
      requestId?: number | null;
      reason?: string;
      targetVelocity?: number;
      targetAngle?: number;
      mode?: CommandType;
      cost1?: number;
      code?: 'monitor_remote_control_action' | 'monitor_remote_control_shortcut' | 'monitor_remote_control_safety';
    } = {},
  ) => {
    reportSgmEvent({
      type,
      code:
        options.code ||
        (trigger === 'keyboard'
          ? 'monitor_remote_control_shortcut'
          : 'monitor_remote_control_action'),
      msg: {
        page: 'remote_control',
        vehicleName,
        action,
        trigger,
        requestId: options.requestId ?? null,
        result,
        reason: options.reason,
        key: options.key,
        targetVelocity: options.targetVelocity,
        targetAngle: options.targetAngle,
        mode: options.mode || commandType,
      },
      cost1: options.cost1,
    });
  };

  const handleSendControlRequest = (
    speed: number,
    angle: number,
    cType?: CommandType,
  ) => {
    const direc = directArr[currentSliderIndex];
    const timeStamp = moment().format('YYYY-MM-DD HH:mm:ss');
    requestId = requestId + 1;
    localStorage.setItem('JDX_REMOTE_CONTROL_REQUEST_ID', String(requestId));
    const requestOpt = {
      id: requestId,
      vehicleName,
      targetVelocity: speed * direc,
      targetAngle: (angle * Math.PI) / 180,
      timeStamp,
      commandType: cType || commandType,
      moduleName: ModuleName.SUPERVISOR,
    };
    const data = eventProvider.makeRequestEvent(
      AllActionType.REMOTE_REQUEST_REMOTE_CONTROL,
      requestOpt,
    );
    handleSendRemoteRequest(data);
  };

  /**
   * 用户操作指令下发
   * @param {number} speed 速度
   * @param {number} angle 角度
   * @param {CommandType} cType 遥控类型
   */
  const handleRecordOperation = (
    speed: number,
    angle: number,
    operationInfo?: OperationInfo,
  ) => {
    const direc = directArr[currentSliderIndex];
    const timeStamp = moment().format('YYYY-MM-DD HH:mm:ss');
    requestId = requestId + 1;

    const requestOpt = {
      id: requestId,
      vehicleName,
      targetVelocity: speed * direc,
      targetAngle: (angle * Math.PI) / 180,
      timeStamp,
      commandType: commandType,
      moduleName: ModuleName.SUPERVISOR,
    };

    const data = eventProvider.makeRequestEvent(
      AllActionType.REMOTE_REQUEST_USER_CONTROL,
      requestOpt,
    );
    const sent = handleSendRemoteRequest(data) === true;
    if (operationInfo) {
      reportRemoteControlAction(
        sent ? SgmEventType.INFO : SgmEventType.WARN,
        operationInfo.action,
        operationInfo.trigger,
        sent ? 'sent' : 'blocked',
        {
          key: operationInfo.key,
          requestId: requestOpt.id,
          targetVelocity: requestOpt.targetVelocity,
          targetAngle: angle,
          mode: requestOpt.commandType,
          reason: sent ? undefined : 'websocket_not_open',
        },
      );
    }
  };

  // 取消异常状态
  const handleResetUnusualState = () => {
    if (responseData) {
      const { state, commandDelay } = responseData;
      if (
        state === ControlState.TIME_EXCEPTION &&
        commandDelay < controlResponseDelay
      ) {
        const data = eventProvider.makeRequestEvent(
          AllActionType.REMOTE_REQUEST_RESET_ABNORMAL_CONTROL,
          {
            id: requestId,
            commandType: commandType,
            moduleName: ModuleName.SUPERVISOR,
            timeStamp: moment().format('YYYY-MM-DD HH:mm:ss'),
            vehicleName: vehicleName,
          },
        );
        handleSendRemoteRequest(data);
      }
    }
  };

  const showTips = (mes: string, type: string = 'warning') => {
    const noticeNode = document.querySelector('.ant-message-notice');
    if (noticeNode) {
      return;
    }
    if (type === 'warning') {
      message.warning(mes);
    }
  };

  const handleClickSpeedControlBtn = (
    index: number,
    trigger: ActionTrigger = 'click',
    key?: string,
  ) => {
    currentSliderIndex = index;
    handleRecordOperation(speed, angle, {
      action: gearActionList[index],
      trigger,
      key,
    });
    if (index == 1) {
      // 空挡键速度归零Cli
      setSpeed(0);
      resetSpeedDashboard();
    }
    forceUpdate(!updateFlag);
  };

  // 速度角度归0
  const handleResetAngleAndSpeed = () => {
    setAngle(0);
    setSpeed(0);
    currentSliderIndex = 1;
  };

  // 重置speedDashboard显示刻度
  const resetSpeedDashboard = () => {
    speedDashboardRef &&
      speedDashboardRef.current &&
      speedDashboardRef.current.resetSpeed();
  };

  // 重置angleDashboard显示刻度
  const resetAngleDashboard = () => {
    angleDashboardRef &&
      angleDashboardRef.current &&
      angleDashboardRef.current.resetAngle();
  };

  // 重置dashboard刻度
  const resetDashborad = () => {
    resetSpeedDashboard();
    resetAngleDashboard();
  };

  useEffect(() => {
    if (responseData) {
      const { id, responseTimeStamp, state } = responseData;
      responseMap.set(id, responseTimeStamp);
      if (state === ControlState.TIME_EXCEPTION) {
        if (timeoutRequestIdRef.current !== id) {
          timeoutRequestIdRef.current = id;
          reportRemoteControlAction(SgmEventType.ERROR, 'command_timeout', 'system', 'timeout', {
            requestId: id,
            reason: 'control_time_exception',
            code: 'monitor_remote_control_safety',
          });
        }
        currentSliderIndex = 1;
        resetSpeedDashboard();
        setSpeed(0);
      }
      handleResetUnusualState();
    }
  }, [JSON.stringify(responseData)]);

  useEffect(() => {
    const storageRequestId = localStorage.getItem(
      'JDX_REMOTE_CONTROL_REQUEST_ID',
    );
    if (storageRequestId) {
      requestId = parseInt(storageRequestId);
    }
  }, []);

  useEffect(() => {
    handleSendControlRequest(speed, angle, commandType);
    timer = setInterval(() => {
      handleSendControlRequest(speed, angle, commandType);
    }, controlRequestDuration);
    return () => {
      clearInterval(timer);
      timer = null;
    };
  }, [speed, angle, commandType]);

  useEffect(() => {
    if (responseData) {
      const { delayInfo, commandDelay } = responseData;
      if (delayInfo) {
        if (commandDelay > controlResponseDelay) {
          showTips('当前指令延时严重');
          handleResetAngleAndSpeed();
          resetDashborad();
          reportRemoteControlAction(
            SgmEventType.WARN,
            'auto_reset_zero',
            'system',
            'executed',
            {
              requestId: responseData.id || null,
              reason: 'command_delay_high',
              code: 'monitor_remote_control_safety',
            },
          );
        }
      }
    }
  }, [JSON.stringify(responseData)]);

  useEffect(() => {
    // 在强制遥控模式下监测视频和3D延时是否大于阈值，是的话手动回正并提示
    if (commandType === CommandType.SUPER) {
      if (
        maxVideoDelay > maxVisibleDelayLimit &&
        localViewDelay > maxVisibleDelayLimit
      ) {
        if (canShowConfirm) {
          setCanShowConfirm(false);
          Modal.destroyAll();
          handleResetAngleAndSpeed();
          resetDashborad();
          reportRemoteControlAction(
            SgmEventType.WARN,
            'force_mode_delay_protection',
            'system',
            'intervened',
            {
              requestId: responseData?.id || null,
              reason: 'video_and_3d_delay_high',
              code: 'monitor_remote_control_safety',
            },
          );
          Modal.warning({
            closable: true,
            centered: true,
            maskClosable: false,
            content: '当前视频、3d延时严重，建议您关闭强制远程操控',
            okText: '确定',
            onOk: () => {
              setCanShowConfirm(true);
            },
            onCancel: () => {
              setCanShowConfirm(false);
            },
          });
        }
      }
    }
  }, [maxVideoDelay, localViewDelay, canShowConfirm]);

  const handleChangeControlMode = useCallback(
    (activeMode: ButtonItem) => {
      let content;
      const newBtnGroup = btnGroup.map((item: ButtonItem) => {
        item.isActive = item.value === activeMode.value;
        return item;
      });

      if (activeMode.value === CommandType.NORMAL) {
        reportRemoteControlAction(
          SgmEventType.INFO,
          'mode_normal',
          'click',
          'selected',
          {
            targetVelocity: 0,
            targetAngle: 0,
            mode: activeMode.value,
          },
        );
        setCommandType(activeMode.value);
        setBtnGroup(newBtnGroup);
        resetDashborad();
        handleResetAngleAndSpeed();
        return;
      }
      if (!responseData) {
        showTips('当前无法强制遥控，请稍后尝试');
        reportRemoteControlAction(SgmEventType.WARN, 'mode_super', 'click', 'blocked', {
          reason: 'control_response_unavailable',
          mode: activeMode.value,
        });
        return;
      }
      if (
        maxVideoDelay > maxVisibleDelayLimit &&
        localViewDelay > maxVisibleDelayLimit
      ) {
        content = ModalInfoText[1];
      } else {
        content = ModalInfoText[0];
      }

      reportRemoteControlAction(
        SgmEventType.INFO,
        'mode_super',
        'click',
        'confirmation_opened',
        {
          mode: activeMode.value,
        },
      );

      Modal.confirm({
        className: 'contorl-confirm-Modal',
        centered: true,
        maskClosable: false,
        content: content,
        onOk: () => {
          reportRemoteControlAction(
            SgmEventType.INFO,
            'mode_super_confirm',
            'click',
            'confirmed',
            {
              mode: activeMode.value,
            },
          );
          setBtnGroup(newBtnGroup);
          setCommandType(activeMode.value);
          resetDashborad();
          handleResetAngleAndSpeed();
        },
        onCancel: () => {
          reportRemoteControlAction(SgmEventType.WARN, 'mode_super_confirm', 'click', 'cancelled', {
            reason: 'user_cancelled',
            mode: activeMode.value,
          });
        },
      });
    },
    [JSON.stringify(responseData)],
  );

  const handleEmergencyBrake = async () => {
    const startedAt = Date.now();
    const params = {
      path: '/monitor/web/command/emergency_brake',
      method: 'POST',
      body: {
        timeStamp: moment().format('YYYY-MM-DD HH:mm:ss'),
        vehicleName: vehicleName,
      },
    };
    try {
      const res = await request(params);
      if (res.code === HttpStatusCode.Success) {
        reportRemoteControlAction(SgmEventType.INFO, 'emergency_brake_http', 'system', 'success', {
          requestId: null,
          code: 'monitor_remote_control_safety',
          cost1: Date.now() - startedAt,
        });
      } else {
        reportRemoteControlAction(SgmEventType.ERROR, 'emergency_brake_http', 'system', 'failed', {
          requestId: null,
          reason: 'business_failed',
          code: 'monitor_remote_control_safety',
          cost1: Date.now() - startedAt,
        });
        message.error(res.message);
      }
    } catch {
      reportRemoteControlAction(SgmEventType.ERROR, 'emergency_brake_http', 'system', 'failed', {
        requestId: null,
        reason: 'request_error',
        code: 'monitor_remote_control_safety',
        cost1: Date.now() - startedAt,
      });
    }
  };
  const onChange = useCallback(
    (
      type: ControlType,
      result: number,
      operation?: ControlSpeed | ControlDirection,
      trigger: ActionTrigger = 'click',
      key?: string,
    ) => {
      if (type === ControlType.SPEED) {
        setSpeed(result);
        if (operation === ControlSpeed.BRAKE) {
          currentSliderIndex = 1;
          handleResetAngleAndSpeed();
          resetDashborad();
          reportRemoteControlAction(SgmEventType.INFO, speedActionMap[operation], trigger, 'requested', {
            key,
            requestId,
            targetVelocity: 0,
            targetAngle: 0,
          });
          handleEmergencyBrake();
        } else {
          handleRecordOperation(result, angle, {
            action: speedActionMap[operation as ControlSpeed],
            trigger,
            key,
          });
        }
      } else if (type === ControlType.ANGLE) {
        setAngle(result);
        handleRecordOperation(speed, result, {
          action: angleActionMap[operation as ControlDirection],
          trigger,
          key,
        });
      }
    },
    [speed, angle, commandType, currentSliderIndex],
  );

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    currentSliderIndex = newIndex;
    setSpeedControl(arrayMove(speedControl, oldIndex, newIndex));
    handleRecordOperation(speed, angle, {
      action: gearActionList[newIndex],
      trigger: 'drag',
    });
  };

  const handleDealWithKeyboardEventCode = (event: any) => {
    const keyCode = event.code;
    switch (keyCode) {
      case 'KeyD':
        handleClickSpeedControlBtn(0, 'keyboard', keyCode);
        break;
      case 'KeyN':
        handleClickSpeedControlBtn(1, 'keyboard', keyCode);
        break;
      case 'KeyR':
        handleClickSpeedControlBtn(2, 'keyboard', keyCode);
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (!visible) {
      document.addEventListener('keyup', handleDealWithKeyboardEventCode);
    } else {
      document.removeEventListener('keyup', handleDealWithKeyboardEventCode);
      handleResetAngleAndSpeed();
      resetDashborad();
      handleSendControlRequest(0, 0, CommandType.NORMAL);
      handleRecordOperation(0, 0);
    }
    return () => {
      document.removeEventListener('keyup', handleDealWithKeyboardEventCode);
    };
  }, [visible]);

  useEffect(() => {
    const closeControlPage = () => {
      handleResetAngleAndSpeed();
      handleSendControlRequest(0, 0, CommandType.NORMAL);
      handleRecordOperation(0, 0);
    };

    window.onunload = () => {
      closeControlPage();
    };
    return () => {
      timer && clearInterval(timer);
      closeControlPage();
      window.onunload = null;
      localStorage.removeItem('JDX_REMOTE_CONTROL_REQUEST_ID');
      requestId = 0;
    };
  }, []);

  return (
    <div className='dashboard-container'>
      <div className='dashboard-head'>
        <ButtonRadio
          btnGroup={btnGroup}
          needConfirm={true}
          onChange={handleChangeControlMode}
        />
        <div className='remote-control-mode'>
          <i className={tipMessageMap.get(commandType)!.iconCls}></i>
          <span>{tipMessageMap.get(commandType)!.message}</span>
        </div>
        {responseData && responseData.state === ControlState.TIME_EXCEPTION && (
          <div className='communication-status'>
            <i className='error-icon'></i>
            通讯异常
          </div>
        )}
      </div>
      <div className='control-content'>
        <div className='speed-control-track'>
          <Sortable lockAxis={'y'} items={speedControl} onSortEnd={onSortEnd} />
          <div className='direction-control'>
            {speedControlBtnList.map((cls: string, index: number) => (
              <i
                onClick={() => handleClickSpeedControlBtn(index)}
                key={index}
                className={cls}
              />
            ))}
          </div>
        </div>
        <div className='dashboard-wrapper'>
          <AngleDashboard
            ref={angleDashboardRef}
            heading={responseData && responseData.heading}
            onChange={onChange}
            visible={visible}
          />
          <SpeedDashboard
            ref={speedDashboardRef}
            realtimeSpeed={responseData && responseData.speed}
            maxLimit={MaxSpeedMap.get(commandType) || 0.8}
            onChange={onChange}
            visible={visible}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Control);
